import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/firebaseServices';
import './blogcss/blog.css';

// Local images
import wholeFoodsPlateImage from './blogimg/whatVitaminsShouldITake/whole_foods_plate.jpg';
import sunlightFishMilkImage from './blogimg/whatVitaminsShouldITake/sunlight_fish_milk.jpg';
import meatFishEggsB12Image from './blogimg/whatVitaminsShouldITake/meat_fish_eggs_b12.jpg';
import leafyGreensLegumesFolateImage from './blogimg/whatVitaminsShouldITake/leafy_greens_legumes_folate.jpg';
import citrusPeppersBroccoliVitCImage from './blogimg/whatVitaminsShouldITake/citrus_peppers_broccoli_vit_c.jpg';
import carrotsSweetPotatoesLiverVitAImage from './blogimg/whatVitaminsShouldITake/carrots_sweet_potatoes_liver_vit_a.jpg';
import nutsSeedsOilsVitEImage from './blogimg/whatVitaminsShouldITake/nuts_seeds_oils_vit_e.jpg';
import kaleSpinachBroccoliVitKImage from './blogimg/whatVitaminsShouldITake/kale_spinach_broccoli_vit_k.jpg';
import diversePeopleAgesImage from './blogimg/whatVitaminsShouldITake/diverse_people_ages.jpg';
import doctorConsultationBloodTestImage from './blogimg/whatVitaminsShouldITake/doctor_consultation_blood_test.jpg';
import warningSignPillsImage from './blogimg/whatVitaminsShouldITake/warning_sign_pills.jpg';

/**
 * Maps filenames from Firestore to local images.
 */
function getLocalImageSource(filename) {
  const commaIndex = filename.indexOf(',');
  if (commaIndex !== -1) {
    filename = filename.substring(0, commaIndex);
  }
  const parts = filename.split(/[/\\]/);
  const baseName = parts[parts.length - 1].trim().toLowerCase();

  switch (baseName) {
    case 'whole_foods_plate.jpg':
      return wholeFoodsPlateImage;
    case 'sunlight_fish_milk.jpg':
      return sunlightFishMilkImage;
    case 'meat_fish_eggs_b12.jpg':
      return meatFishEggsB12Image;
    case 'leafy_greens_legumes_folate.jpg':
      return leafyGreensLegumesFolateImage;
    case 'citrus_peppers_broccoli_vit_c.jpg':
      return citrusPeppersBroccoliVitCImage;
    case 'carrots_sweet_potatoes_liver_vit_a.jpg':
      return carrotsSweetPotatoesLiverVitAImage;
    case 'nuts_seeds_oils_vit_e.jpg':
      return nutsSeedsOilsVitEImage;
    case 'kale_spinach_broccoli_vit_k.jpg':
      return kaleSpinachBroccoliVitKImage;
    case 'diverse_people_ages.jpg':
      return diversePeopleAgesImage;
    case 'doctor_consultation_blood_test.jpg':
      return doctorConsultationBloodTestImage;
    case 'warning_sign_pills.jpg':
      return warningSignPillsImage;
    default:
      return null;
  }
}

const WhatVitaminsShouldITakePage = () => {
  const [article, setArticle] = useState(null);
  const [timestamp, setTimestamp] = useState(null); // <-- Timestamp field name from Firestore

  // 1️⃣ Add Meta Tags Dynamically
  useEffect(() => {
    // Set the document title
    document.title = "What Vitamins Should I Take? Essential Info for a Balanced Diet";

    // Helper function to add or update meta tags
    const setMetaTag = (attrName, contentValue, useProperty = false) => {
      const key = useProperty ? 'property' : 'name';
      let metaTag = document.querySelector(`meta[${key}="${attrName}"]`);

      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute(key, attrName);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', contentValue);
    };

    // Standard meta tags
    setMetaTag(
      "description",
      "Explore the health benefits of essential vitamins, their dietary sources, and who may need supplementation. Learn how a balanced diet can help meet your nutritional needs."
    );
    setMetaTag(
      "keywords",
      "vitamins, supplementation, balanced diet, health, deficiency, vitamin D, vitamin B12, folate, nutrition"
    );

    // Open Graph tags
    setMetaTag(
      "og:title",
      "What Vitamins Should I Take? Essential Info for a Balanced Diet",
      true
    );
    setMetaTag(
      "og:description",
      "Understand the roles of key vitamins, who needs them, and how to maintain a nutrient-rich diet that supports overall health and wellness.",
      true
    );
    setMetaTag(
      "og:image",
      "https://purehealthnexus.com/assets/images/whole_foods_plate.jpg",
      true
    );
    setMetaTag(
      "og:url",
      "https://purehealthnexus.com/what-vitamins-should-i-take",
      true
    );

    // Twitter card
    setMetaTag("twitter:card", "summary_large_image");

    // Cleanup on unmount (optional)
    return () => {
      document.title = "Pure Health Nexus";
    };
  }, []);

  // 2️⃣ Fetch the Article from Firestore
  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const colRef = collection(
          db,
          'products',
          'blog',
          'whatvitaminsshoulditake'
        );
        const snapshot = await getDocs(colRef);

        if (!snapshot.empty) {
          const docData = snapshot.docs[0].data();
          setArticle(docData);
          setTimestamp(docData.eutimestamp); // <-- Timestamp field name from Firestore
        } else {
          // handle no doc scenario
        }
      } catch (error) {
        // handle error
      }
    };

    fetchArticle();
  }, []);

  if (!article) {
    return <div>Loading...</div>;
  }

  // 3️⃣ Organize Sections for Conclusion & References
  let conclusionSection = null;
  let referenceBlock = null;
  let otherBlocks = [];

  if (article.sections && article.sections.length > 0) {
    article.sections.forEach((section, sectionIndex) => {
      if (section.blocks && section.blocks.length > 0) {
        // If the first block is an h2 titled "Conclusion", set as conclusion
        if (section.blocks[0].tag === 'h2' && section.blocks[0].text === 'Conclusion') {
          conclusionSection = section;
        }
        // If it's the last section, set as references
        else if (sectionIndex === article.sections.length - 1) {
          referenceBlock = section.blocks;
        } else {
          otherBlocks.push(section);
        }
      }
    });
  }

  // 4️⃣ Render Block Function
  const renderBlock = (block, index, isReference = false) => {
    const { tag, text } = block;
    const style = isReference ? { fontSize: '10px', margin: '0' } : {};

    switch (tag) {
      case 'h1':
        return (
          <h1 key={index} className="recipe-h1" style={style}>
            {text}
            <style>{`.recipe-h1::after { left: 8%; }`}</style>
          </h1>
          // Add Updated: {Date here} Month Day, Year
        );
      case 'h2':
        return (
          <React.Fragment key={index}>
            <hr
              style={{ width: '75%', margin: '20px auto', border: '1px solid #e67e00' }}
            />
            <h2 className="recipe-h2" style={style}>
              <span>{text}</span>
            </h2>
          </React.Fragment>
        );
      case 'h3':
        return (
          <React.Fragment key={index}>
            <hr
              style={{ width: '75%', margin: '20px auto', border: '1px solid #e67e00' }}
            />
            <h3 className="recipe-h3" style={style}>
              {text}
            </h3>
          </React.Fragment>
        );
      case 'p':
        return (
          <p key={index} className="recipe-p" style={style}>
            {text}
          </p>
        );
      case 'img': {
        const localImg = getLocalImageSource(text);
        return (
          <div key={index} className="recipe-img">
            <img
              src={localImg ?? text}
              alt={text}
              style={{ maxHeight: '500px', width: 'auto' }}
            />
          </div>
        );
      }
      case 'ul':
        return (
          <ul key={index} className="recipe-ul" style={style}>
            {Array.isArray(text)
              ? text.map((li, liIndex) => <li key={liIndex}>{li}</li>)
              : <li>{text}</li>}
          </ul>
        );
      case 'ol':
        return (
          <ol key={index} className="recipe-ol" style={style}>
            {Array.isArray(text) &&
              text.map((li, liIndex) => {
                if (typeof li === 'object') {
                  return (
                    <li key={liIndex}>
                      {li.text}
                      {li.nested && (
                        <ul>
                          {li.nested.map((nestedItem, nestedIndex) => (
                            <li key={nestedIndex}>{nestedItem}</li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                } else {
                  return <li key={liIndex}>{li}</li>;
                }
              })}
          </ol>
        );
      case 'hr':
        return (
          <hr
            key={index}
            style={{ width: '75%', margin: '20px auto', border: '1px solid #e67e00' }}
          />
        );
      default:
        return (
          <div key={index} style={style}>
            {text}
          </div>
        );
    }
  };

  // Format the timestamp for display//<-- Timestamp field name from Firestore
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };
  
  // 5️⃣ Render the Page
  return (
    <div className="container">
      <div className="calming-morning-routine">
        {article.h1 && (
          <h1 className="recipe-h1" style={{ position: 'relative' }}>
            {article.h1}
            <style>{`.recipe-h1::after { left: 8%; }`}</style>
          </h1>
        )}
        {timestamp && (
          <p className="timestamp" style={{ textAlign: 'center', color: '#888' }}>
            Updated: {formatTimestamp(timestamp)}
          </p>
        )}

        {/* Render Other Blocks (excluding Conclusion & References) */}
        {otherBlocks.map((section, sectionIndex) => (
          <section key={sectionIndex}>
            {section.blocks &&
              section.blocks.map((block, blockIndex) => renderBlock(block, blockIndex))}
          </section>
        ))}

        {/* Conclusion Section */}
        {conclusionSection && (
          <section>
            {conclusionSection.blocks.map((block, index) => renderBlock(block, index))}
          </section>
        )}

        {/* CTA Button */}
        <button
          className="recipe-button"
          onClick={() => (window.location.href = 'https://purehealthnexus.com/top-rated-vitamins')}
        >
          See Top-Rated Vitamins Here
        </button>

        {/* References Section after Button */}
        {referenceBlock &&
          referenceBlock.map((block, index) => (
            <div className="notranslate" key={index}>
              {renderBlock(block, index, true)}
            </div>
          ))}
        </div>
    </div>
  );
};

export default WhatVitaminsShouldITakePage;
